/**
 * APCalendar Component
 *
 * Lightweight wrapper around react-day-picker to reuse its calendar logic
 * while exposing a design-system-friendly API for internal consumers.
 */

import React, { useMemo } from 'react';

import { isAfter, isBefore } from 'date-fns';
import { DayPicker } from 'react-day-picker';

import { API18n } from '@shared/utils/i18n/i18n';

import type { IAPCalendarProps } from './types';
import type { DayPickerRange, Mode } from 'react-day-picker';

import 'react-day-picker/style.css';
import './styles.scss';

// Matcher type definition (not exported from react-day-picker in some versions)
type Matcher =
  | boolean
  | ((date: Date) => boolean)
  | Date
  | Date[]
  | { from: Date; to: Date }
  | { before: Date }
  | { after: Date }
  | { before: Date; after: Date }
  | { dayOfWeek: number | number[] };

const getDateFnsLocale = () => {
  try {
    return API18n.getDateFnsLanguage();
  } catch {
    return undefined;
  }
};

export const APCalendar: React.FC<IAPCalendarProps> = ({
  month,
  selectionMode = 'single',
  selectedDate,
  selectedRange,
  minDate,
  maxDate,
  isDateDisabled,
  onSelectDate,
  onMonthChange,
  size = 'medium',
  'data-testid': testId,
}) => {
  const locale = useMemo(() => getDateFnsLocale(), []);

  const handleSelectSingle = (date?: Date) => {
    if (!date) return;
    if (minDate && isBefore(date, minDate)) return;
    if (maxDate && isAfter(date, maxDate)) return;
    if (isDateDisabled?.(date)) return;
    onSelectDate?.(date);
  };

  const handleMonthChange = (nextMonth: Date) => {
    onMonthChange?.(nextMonth);
  };

  const disabled = useMemo<Matcher | Matcher[] | undefined>(() => {
    const matchers: Matcher[] = [];

    if (minDate) {
      // Disable all days before the minimum allowed date
      matchers.push({ before: minDate });
    }

    if (maxDate) {
      // Disable all days after the maximum allowed date
      matchers.push({ after: maxDate });
    }

    if (isDateDisabled) {
      // Reuse consumer-provided matcher to visually disable custom dates
      matchers.push((date: Date) => isDateDisabled(date));
    }

    if (!matchers.length) {
      return undefined;
    }

    return matchers.length === 1 ? matchers[0] : matchers;
  }, [minDate, maxDate, isDateDisabled]);

  return (
    <div className={`ap-calendar ap-calendar--${size}`} data-testid={testId}>
      <DayPicker
        required={selectionMode === 'range'}
        mode={selectionMode as Mode}
        locale={locale}
        month={month}
        selected={
          selectionMode === 'single'
            ? (selectedDate ?? undefined)
            : {
                from: selectedRange?.from ?? undefined,
                to: selectedRange?.to ?? undefined,
              }
        }
        onSelect={
          selectionMode === 'single'
            ? (value: Date | DayPickerRange | Date[] | undefined) =>
                handleSelectSingle(value as Date | undefined)
            : undefined
        }
        navLayout="after"
        captionLayout="dropdown"
        onMonthChange={handleMonthChange}
        showOutsideDays
        fixedWeeks
        disabled={disabled}
        className="ap-calendar__daypicker"
      />
    </div>
  );
};

APCalendar.displayName = 'APCalendar';

export type { IAPCalendarProps };
export default APCalendar;
